import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '@/theme/scalingTokens';

// spec 104 US5 (FR-016..FR-019): overlay open/close animation.
// 180 ms / ease-out is the locked-in default duration (see spec
// Assumptions); 100 ms is the reduced-motion fallback applied to the
// backdrop only (no scale).
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';
const ENTER_DURATION = '180ms';
const EXIT_DURATION = '180ms';
const REDUCED_MOTION_DURATION = '100ms';
const ANIMATION_EASING = 'ease-out';

/**
 * Number of milliseconds the parent should wait between requesting close
 * and unmounting the dialog. Equal to {@link EXIT_DURATION} (default
 * motion) and {@link REDUCED_MOTION_DURATION} when the user prefers
 * reduced motion. Exported so {@link EventRequestDetails} can defer its
 * own `onClose` callback by exactly this amount.
 */
export const EVENT_REQUEST_DETAILS_EXIT_MS = 180;
export const EVENT_REQUEST_DETAILS_EXIT_REDUCED_MS = 100;

export const useEventRequestDetailsStyles = makeStyles({
  surface: {
    maxWidth: '892px',
    width: '95vw',
    maxHeight: '90vh',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow64,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflowY: 'hidden',
    willChange: 'transform, opacity',
    // Open animation: subtle scale-up (95% → 100%) + fade-in. The exit
    // counterpart is applied via the `surfaceExiting` class. Reduced-motion
    // users get no animation on the surface (the backdrop still fades).
    animationName: {
      from: { transform: 'scale(0.95)', opacity: 0 },
      to: { transform: 'scale(1)', opacity: 1 },
    },
    animationDuration: ENTER_DURATION,
    animationTimingFunction: ANIMATION_EASING,
    animationFillMode: 'both',
    [REDUCED_MOTION]: {
      animationName: 'none',
      animationDuration: '0ms',
    },
    [mq.tablet]: {
      maxWidth: '95vw',
    },
    [mq.mobile]: {
      maxWidth: '100vw',
      width: '100vw',
      maxHeight: '100vh',
      ...shorthands.borderRadius('0'),
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflowY: 'hidden',
    [mq.mobile]: {
      maxHeight: '100vh',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...shorthands.padding(tokens.spacingVerticalM, '0'),
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minHeight: '40px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.padding('0', '0', '0', tokens.spacingHorizontalXXL),
    ...shorthands.gap(tokens.spacingHorizontalL),
    flexShrink: 0,
  },
  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('0', tokens.spacingHorizontalM),
    flexShrink: 0,
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    ...shorthands.padding('0', tokens.spacingHorizontalL, '0', '0'),
    flexGrow: 1,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: fontScale.subtitle,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  requestId: {
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('0', '0', '0', tokens.spacingHorizontalXXL),
  },
  closeButton: {
    flexShrink: 0,
  },
  content: {
    ...shorthands.padding(tokens.spacingVerticalXXL),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
    overflowY: 'auto',
    flexGrow: 1,
    backgroundColor: tokens.colorNeutralBackground3,
    [mq.mobile]: {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
  },
  spaceSection: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalXXL),
    [mq.tablet]: {
      flexDirection: 'column',
    },
  },
  spaceImage: {
    width: '160px',
    height: '135px',
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    objectFit: 'cover',
    flexShrink: 0,
    [mq.tablet]: {
      width: '100%',
      height: '140px',
    },
  },
  spaceDetails: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  spaceDetailLine: {
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  spaceDetailLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  spaceName: {
    fontSize: fontScale.subtitle,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalL,
    [mq.tablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  fieldGrid3Col: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalL,
    [mq.tablet]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
    },
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.margin(tokens.spacingVerticalXS, '0'),
  },
  contactsList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalXXL),
    [mq.mobile]: {
      flexDirection: 'column',
      ...shorthands.gap(tokens.spacingVerticalM),
    },
  },
  contactItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  contactsLabel: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    display: 'block',
  },
  contactName: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  contactEmail: {
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  eventCoordinationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(8.75rem, 1fr) minmax(0, 2fr)',
    alignItems: 'start',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalXS,
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
    },
  },
  eventCoordinationLabel: {
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  eventCoordinationLinks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
    ...shorthands.gap(tokens.spacingVerticalXXS),
    wordBreak: 'break-word',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    ...shorthands.padding(tokens.spacingVerticalXXL),
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    ...shorthands.padding(tokens.spacingVerticalXXL),
    ...shorthands.gap(tokens.spacingVerticalM),
    textAlign: 'center',
  },
  errorTitle: {
    fontSize: fontScale.subtitle,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
  errorDescription: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
  },
  placeholderIcon: {
    color: tokens.colorNeutralForeground4,
  },
  additionalGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalL,
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
    },
  },
  sectionHeaderWithAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: tokens.spacingVerticalS,
  },
  additionalText: {
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  additionalTextLabel: {
    fontWeight: tokens.fontWeightSemibold,
    display: 'block',
    marginBottom: tokens.spacingVerticalXXS,
  },
  // spec 104 US5: exit animation applied while the dialog is animating
  // out (parent defers the actual unmount by EVENT_REQUEST_DETAILS_EXIT_MS).
  surfaceExiting: {
    animationName: {
      from: { transform: 'scale(1)', opacity: 1 },
      to: { transform: 'scale(0.95)', opacity: 0 },
    },
    animationDuration: EXIT_DURATION,
    animationTimingFunction: ANIMATION_EASING,
    animationFillMode: 'forwards',
    [REDUCED_MOTION]: {
      animationName: 'none',
      animationDuration: '0ms',
      opacity: 0,
    },
  },
  backdrop: {
    backgroundColor: tokens.colorBackgroundOverlay,
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: ENTER_DURATION,
    animationTimingFunction: ANIMATION_EASING,
    animationFillMode: 'both',
    [REDUCED_MOTION]: {
      animationDuration: REDUCED_MOTION_DURATION,
    },
  },
  backdropExiting: {
    animationName: {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
    animationDuration: EXIT_DURATION,
    animationTimingFunction: ANIMATION_EASING,
    animationFillMode: 'forwards',
    [REDUCED_MOTION]: {
      animationDuration: REDUCED_MOTION_DURATION,
    },
  },
});
